/**
 * The display user a session token names. Its claims are backend data, so a token whose payload
 * does not parse is reported like any answer that breaks its schema.
 */

import { z } from 'zod';

import { SECOND_MS } from '@/lib/timeUnits';
import type { AuthUser } from '@/types/auth';
import { SessionClaimsSchema, SessionExpirySchema } from '@/types/auth';
import { ViolationSourceKind } from '@/types/contract';

import { ContractError, getAnswerOrigin, JsonTextSchema, parseOne } from './contract';
import { HTTP_STATUS } from './httpStatus';

// /setup and /invite/accept sign their tokens as this route does.
const SESSION_URL = '/vms/api/v1/auth/session';
// atob() wants base64 padded to whole groups of this many characters.
const BASE64_QUANTUM_CHARS = 4;

/** A JWT's payload segment, base64url and unpadded, as the UTF-8 text it encodes. */
const TokenPayloadSchema = z.string().transform((token, ctx): string => {
    const payload = token.split('.')[1];
    if (payload === undefined || payload === '') {
        ctx.issues.push({
            code: 'custom',
            message: 'Not a JWT',
            input: token,
        });
        return z.NEVER;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (BASE64_QUANTUM_CHARS - (base64.length % BASE64_QUANTUM_CHARS)) %
        BASE64_QUANTUM_CHARS;
    try {
        // atob() yields a binary (Latin-1) string; the bytes are UTF-8, so a name with accents,
        // CJK or emoji decodes whole.
        const bytes = Uint8Array.from(
            atob(base64 + '='.repeat(padding)), (char) => char.charCodeAt(0),
        );
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        ctx.issues.push({
            code: 'custom',
            message: 'The payload is not base64url UTF-8',
            input: payload,
        });
        return z.NEVER;
    }
});

const SessionUserSchema = TokenPayloadSchema.pipe(JsonTextSchema).pipe(SessionClaimsSchema);
const SessionExpiryClaimSchema = TokenPayloadSchema.pipe(JsonTextSchema).pipe(SessionExpirySchema);

/** The user the token names, or null once a token that breaks its contract is reported. */
export function getSessionUser(token: string): AuthUser | null {
    const origin = getAnswerOrigin(SESSION_URL, HTTP_STATUS.OK, {
        kind: ViolationSourceKind.Cloud,
    });
    try {
        return parseOne(SessionUserSchema, token, origin);
    } catch (error) {
        if (error instanceof ContractError) {
            return null;
        }
        throw error;
    }
}

/** When the token expires, in Unix milliseconds, or null once a broken token is reported. */
export function getSessionExpiry(token: string): number | null {
    const origin = getAnswerOrigin(SESSION_URL, HTTP_STATUS.OK, {
        kind: ViolationSourceKind.Cloud,
    });
    try {
        return parseOne(SessionExpiryClaimSchema, token, origin).exp * SECOND_MS;
    } catch (error) {
        if (error instanceof ContractError) {
            return null;
        }
        throw error;
    }
}
